import { Injectable } from '@angular/core'
import { Store } from '@ngrx/store'
import { Subscription, timer } from 'rxjs'
import { share, takeUntil } from 'rxjs/operators'

import { AAAStore } from '../../store/root-reducer'
import { memberInfoBackgroundPoll, quickRenewFinalCheck } from './member.actions'

const POLL_INTERVAL = 30000
const OVERALL_TIMEOUT = 180000

enum TimerStatus {
  Stopped = 0,
  Running,
}

/**
 * Drives the payment-in-process sheet's member-details rechecks while a
 * quick-renew payment is still being processed (DRRWEB-3502): a 30s auto-poll
 * and a 3-minute overall timeout.
 */
@Injectable({
  providedIn: 'root',
})
export class QuickRenewPaymentStatusService {
  private timerStatus = TimerStatus.Stopped
  private pollSubscription: Subscription = null
  private timeoutSubscription: Subscription = null

  constructor(private store$: Store<AAAStore>) {}

  start() {
    if (this.timerStatus === TimerStatus.Running) {
      return null
    }

    this.timerStatus = TimerStatus.Running

    // The overall timeout ends the poll deterministically: the poll's own
    // takeUntil(timeout$) subscribes to timeout$ before the poll's own
    // interval, so at the coincident 180s tick the timeout always wins over
    // relying on task-queue firing order between two independently
    // scheduled timers.
    const timeout$ = timer(OVERALL_TIMEOUT).pipe(share())

    this.pollSubscription = timer(POLL_INTERVAL, POLL_INTERVAL)
      .pipe(takeUntil(timeout$))
      .subscribe(() => this.store$.dispatch(memberInfoBackgroundPoll()))

    this.timeoutSubscription = timeout$.subscribe(() => {
      this.stop()
      this.store$.dispatch(quickRenewFinalCheck())
    })
  }

  stop() {
    if (this.timerStatus === TimerStatus.Stopped) {
      return null
    }

    this.pollSubscription?.unsubscribe()
    this.timeoutSubscription?.unsubscribe()
    this.pollSubscription = null
    this.timeoutSubscription = null

    this.timerStatus = TimerStatus.Stopped
  }
}
